"use client";

import React, { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useLocale } from "next-intl";
import DataLayer from "@/components/data-layer/data-layer";
import { PAGE_DATA_LAYER_MAP } from "@/components/data-layer/page-data-layer-map";
import { AuthStatusProvider, useAuthStatusContext } from "@/providers/AuthStatusProvider";
import { useUserGroups } from "@/hooks/login/useUserGroups";

const PageDataProvider = () => {
  const pathname = usePathname();
  const locale = useLocale();
  const [dataLayerProps, setDataLayerProps] = useState<Record<string, any> | null>(null);
  const hasPushedRef = useRef<string | null>(null);
  const { isAuthenticated, loading } = useAuthStatusContext();
  const { isAgent, isLoading: isGroupsLoading, isError: isGroupsError } = useUserGroups();

  useEffect(() => {
    // Set agent status globally for other components to access
    if (typeof window !== "undefined") {
      window.colletteAgentStatus = {
        isAuthenticated: isAuthenticated ?? false,
        isAgent: isAgent || false
      };
    }

    if (loading || isGroupsLoading || isGroupsError) return;

    const localePrefix = `/${locale}`;
    const path = pathname.startsWith(localePrefix)
      ? pathname.slice(localePrefix.length) || "/"
      : pathname;

    const config = PAGE_DATA_LAYER_MAP[path];

    if (!config) {
      setDataLayerProps(null);
      hasPushedRef.current = null;
      return;
    }

    if (hasPushedRef.current === pathname) return;

    hasPushedRef.current = pathname;

    const pageInformation: Record<string, string> = {
      pageGroupType: config.pageGroupType,
      isAgent: isAuthenticated && isAgent ? "true" : "false"
    };

    if (config.pageSubgroupType) {
      pageInformation.pageSubgroupType = config.pageSubgroupType;
    }

    setDataLayerProps({
      event: "dataLayerPush",
      pageInformation
    });
  }, [pathname, locale, isAuthenticated, isAgent, loading, isGroupsLoading, isGroupsError]);

  return dataLayerProps ? <DataLayer data={dataLayerProps} /> : null;
};

const PageInfoDataLayer = () => (
  <AuthStatusProvider>
    <PageDataProvider />
  </AuthStatusProvider>
);

export default PageInfoDataLayer;
